import { Box, CircularProgress, Fade, Typography } from '@mui/material';
import React from 'react';
import { BuildingAlternative } from '../../stores/SubprojectStore.ts';
import { EnergyMinMax, ExternalEnergy, OnsiteEnergy } from '../types';
import { EnergyUnit } from './EnergyUnit.tsx';
import { isExternalEnergy, normalizeEnergyUnit } from './energyUtils';
import Grid from '@mui/material/Grid2';

interface EnergySelectedCardsProps {
  alternative: BuildingAlternative;
  minMax: EnergyMinMax;
  totalElectricityUsePerYear: number;
  findOnsite: (id: string) => OnsiteEnergy | undefined;
  findExternal: (id: string) => ExternalEnergy | undefined;
  onChangeOnsiteEnergyCount: (techId: string, newCount: number) => void;
  onRemoveExternalEnergy: (energyId: string) => void;
  emptyText: React.ReactNode;
  referenceDataLoading?: boolean;
  readOnly?: boolean;
}

export const EnergySelectedCards: React.FC<EnergySelectedCardsProps> = ({
  alternative,
  minMax,
  totalElectricityUsePerYear,
  findOnsite,
  findExternal,
  onChangeOnsiteEnergyCount,
  onRemoveExternalEnergy,
  emptyText,
  referenceDataLoading,
  readOnly,
}) => {
  const allParams = [
    ...(alternative?.onSiteEnergy?.params || []),
    ...(alternative?.externalEnergy?.params || []),
  ];

  return (
    <Box
      py={1}
      flexGrow={1}
      minHeight={0}
      pr={4}
      sx={{ overflowY: 'scroll' }}
    >
      {allParams.length === 0 && !referenceDataLoading && (
        <Fade in>
          <Box>{emptyText}</Box>
        </Fade>
      )}
      {referenceDataLoading && allParams.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
          <CircularProgress size={20} />
          <Typography variant='body2' color='text.secondary'>
            Loading energy data...
          </Typography>
        </Box>
      )}
      <Grid container rowSpacing={3} columnSpacing={1.5}>
        {allParams.map((p) => {
          const isOnsite = 'techId' in p;
          const techId = isOnsite
            ? p.techId
            : `${p.utilityIdentifier}:${p.ratePlanType}:${p.state}:${p.sector}`;
          const units = 'units' in p ? p.units : 1;
          const unit = isOnsite ? findOnsite(techId) : findExternal(techId);

          if (!unit) {
            return;
          }

          const { normalizedEnergy, normalizedCapital, limit } =
            normalizeEnergyUnit(unit, minMax, totalElectricityUsePerYear, alternative);

          return (
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }} key={unit.id + unit.name}>
              <React.Fragment key={techId}>
                <EnergyUnit
                  unit={unit}
                  inEdit
                  readOnly={readOnly}
                  normalizedEnergy={normalizedEnergy}
                  normalizedCapital={normalizedCapital}
                  onAdd={() => {
                    // Not used in edit mode
                  }}
                  onChange={(value) => {
                    if (!isFinite(value)) {
                      return;
                    }
                    if (!isExternalEnergy(unit)) {
                      onChangeOnsiteEnergyCount(techId, value);
                    }
                  }}
                  onDecrease={() => {
                    if (isExternalEnergy(unit)) {
                      onRemoveExternalEnergy(techId);
                    } else {
                      onChangeOnsiteEnergyCount(techId, units - 1);
                    }
                  }}
                  onIncrease={() => {
                    if (!isExternalEnergy(unit)) {
                      onChangeOnsiteEnergyCount(techId, units + 1);
                    }
                  }}
                  totalCount={units}
                  limit={limit}
                />
              </React.Fragment>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
